import { AGENCY_ROLES, COMPANY_ROLES, USER_ROLES, ADMIN_ROLES } from '../constants/roles.ts'

interface AgencyUser {
  role: string
  [key: string]: any
}

interface CompanyUserAccess {
  company_id: string
  role: string
  can_read: boolean | number
  can_write: boolean | number
}

export interface UserWithRoles {
  is_system_admin?: boolean | number
  agency_users?: AgencyUser[]
  company_user_access?: CompanyUserAccess[]
}

export function isSystemAdmin(user: UserWithRoles | null): boolean {
  return !!user?.is_system_admin
}

export function isAgencyAdmin(user: UserWithRoles | null): boolean {
  return !!user?.agency_users?.some(au => au.role === AGENCY_ROLES.ADMIN)
}

/**
 * Check if user is a Company Admin for a specific company (or any company if no companyId given)
 */
export function isCompanyAdmin(user: UserWithRoles | null, companyId?: string): boolean {
  if (!user?.company_user_access) return false
  if (companyId) {
    return user.company_user_access.some(
      cua => cua.company_id === companyId && cua.role === COMPANY_ROLES.ADMIN,
    )
  }
  return user.company_user_access.some(cua => cua.role === COMPANY_ROLES.ADMIN)
}

/**
 * Check if user is any kind of admin (system, agency, or company)
 */
export function isAnyAdmin(user: UserWithRoles | null): boolean {
  return isSystemAdmin(user) || isAgencyAdmin(user) || isCompanyAdmin(user)
}

export function isAdminRole(role: string | null): boolean {
  return role !== null && (ADMIN_ROLES as Set<string>).has(role)
}

/**
 * Get the effective role a user has for a specific company.
 * Returns the highest-privilege role the user holds.
 */
export function getCompanyRole(
  user: UserWithRoles | null,
  companyId: string,
): 'SYSTEM_ADMIN' | 'AGENCY_ADMIN' | 'COMPANY_ADMIN' | 'MEMBER' | null {
  if (!user) return null
  if (isSystemAdmin(user)) return USER_ROLES.SYSTEM_ADMIN
  if (isAgencyAdmin(user)) return USER_ROLES.AGENCY_ADMIN
  const access = user.company_user_access?.find(cua => cua.company_id === companyId)
  if (!access) return null
  return access.role === COMPANY_ROLES.ADMIN ? USER_ROLES.COMPANY_ADMIN : USER_ROLES.MEMBER
}

/**
 * Check if user can write/edit for a specific company
 */
export function canWriteCompany(user: UserWithRoles | null, companyId: string): boolean {
  if (!user) return false
  if (isSystemAdmin(user)) return true
  if (isAgencyAdmin(user)) return true // Agency admins can write to their companies
  const access = user.company_user_access?.find(cua => cua.company_id === companyId)
  if (!access) return false
  return access.role === COMPANY_ROLES.ADMIN || !!access.can_write
}

/**
 * Check if user is a company-only user (not system admin, not in any agency).
 * These users should be routed directly to their company dashboard, not the agency overview.
 */
export function isCompanyOnlyUser(user: UserWithRoles | null): boolean {
  if (!user) return false
  if (isSystemAdmin(user)) return false
  if (user.agency_users && user.agency_users.length > 0) return false
  return !!(user.company_user_access && user.company_user_access.length > 0)
}

/**
 * Get the first accessible company ID for a company-only user.
 */
export function getFirstCompanyId(user: UserWithRoles | null): string | null {
  if (!user?.company_user_access || user.company_user_access.length === 0) return null
  const readable = user.company_user_access.find(cua => !!cua.can_read)
  return readable?.company_id || user.company_user_access[0].company_id
}

/**
 * Get the appropriate API endpoint prefix based on user role
 */
export function getRoleBasedEndpoint(user: UserWithRoles | null): string {
  if (isSystemAdmin(user)) return '/api/admin'
  if (isAgencyAdmin(user)) return '/api/agency'
  return '/api/user'
}
